import { client } from '../../../client'
import { useEffect, useState } from 'react'
import { Box, Paper, TextField, Typography } from '@material-ui/core'
import { DataGrid, GridColDef } from '@material-ui/data-grid'
import { NotificationBarProvider } from '../../../components/NotificationBar/notificationBarContext'
import { AdminPage } from '../../../layout/AdminPage'
import { RoleCell } from './RoleCell'
import { MenuCell } from './MenuCell'
import { ActiveCell } from './ActiveCell'
import ChevronLeft from '@material-ui/icons/ChevronLeft'

const columns: GridColDef[] = [
  { field: 'firstName', headerName: 'First Name', flex: 0.6 },
  { field: 'lastName', headerName: 'Last Name', flex: 0.6 },
  { field: 'email', headerName: 'Email', flex: 1 },
  {
    field: 'isActive',
    headerName: 'Active',
    flex: 0.4,
    renderCell: ActiveCell
  },
  { field: 'roles', headerName: 'Roles', flex: 1, renderCell: RoleCell },
  {
    field: 'actions',
    headerName: ' ',
    renderCell: MenuCell
  }
]

export type RowData = {
  id: number
  firstName: string
  lastName: string
  email: string
  isActive: boolean
  roles: string[]
}

const formatUserData = (
  data: Awaited<ReturnType<typeof client.getAllUsers>>
): RowData[] => {
  return data.map((u) => ({
    ...u,
    roles: u.roles.map((r) => r.name)
  }))
}

const sortUsers = (users: RowData[]): RowData[] => {
  return [...users].sort((a, b) => {
    // First sort by role priority - check if user has ANY admin/backoffice role
    const aHasAdminRole = a.roles.some(
      (role) =>
        role === 'SUPER_USER' ||
        role === 'BACKOFFICE' ||
        role === 'ADMINISTRATOR'
    )
    const bHasAdminRole = b.roles.some(
      (role) =>
        role === 'SUPER_USER' ||
        role === 'BACKOFFICE' ||
        role === 'ADMINISTRATOR'
    )

    if (aHasAdminRole && !bHasAdminRole) return -1
    if (!aHasAdminRole && bHasAdminRole) return 1

    // If both have same role type, sort by first name
    const aFirstName = a.firstName || ''
    const bFirstName = b.firstName || ''
    return aFirstName.localeCompare(bFirstName)
  })
}

const UserListContent = () => {
  const [users, setUsers] = useState<RowData[]>([])
  const [activeSearch, setActiveSearch] = useState('')
  const [inactiveSearch, setInactiveSearch] = useState('')
  const [activePageSize, setActivePageSize] = useState(25)
  const [inactivePageSize, setInactivePageSize] = useState(25)

  useEffect(() => {
    client.getAllUsers().then(formatUserData).then(setUsers)
  }, [])

  const [deactivatedVisible, setDeactivatedVisible] = useState(false)

  const activeUsers = sortUsers(
    users
      .filter((user) => user.isActive)
      .filter((d) =>
        JSON.stringify(d).toLowerCase().includes(activeSearch.toLowerCase())
      )
  )
  const inactiveUsers = sortUsers(
    users
      .filter((user) => !user.isActive)
      .filter((d) =>
        JSON.stringify(d).toLowerCase().includes(inactiveSearch.toLowerCase())
      )
  )

  return (
    <Paper style={{ width: '100%' }}>
      <NotificationBarProvider>
        <Box style={{ width: '100%', height: 'auto', minHeight: 400 }}>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px'
            }}
          >
            <Typography variant="h5">List of registered users</Typography>
          </Box>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              alignItems: 'center',
              gap: '12px',
              padding: '12px'
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search active users"
              value={activeSearch}
              onChange={(e) => setActiveSearch(e.target.value)}
            />
            <Typography variant="h6">Active Users</Typography>
          </Box>
          <DataGrid
            columns={columns}
            rows={activeUsers}
            pageSize={activePageSize}
            onPageSizeChange={(newPageSize) => setActivePageSize(newPageSize)}
            rowsPerPageOptions={[5, 25, 50, 100]}
            disableSelectionOnClick
            autoHeight
          />

          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px'
            }}
          >
            <h2
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2rem',
                cursor: 'pointer',
                marginTop: '24px'
              }}
              onClick={() => setDeactivatedVisible((v) => !v)}
            >
              Deactivated users{' '}
              <ChevronLeft
                style={{
                  transform: deactivatedVisible
                    ? 'rotate(90deg)'
                    : 'rotate(-90deg)'
                }}
              />
            </h2>

            {deactivatedVisible && (
              <TextField
                variant="outlined"
                placeholder="Search inactive users"
                value={inactiveSearch}
                onChange={(e) => setInactiveSearch(e.target.value)}
              />
            )}
          </Box>
          {deactivatedVisible ? (
            <>
              <DataGrid
                columns={columns}
                rows={inactiveUsers}
                pageSize={inactivePageSize}
                onPageSizeChange={(newPageSize) =>
                  setInactivePageSize(newPageSize)
                }
                rowsPerPageOptions={[5, 25, 50, 100]}
                disableSelectionOnClick
                autoHeight
              />
            </>
          ) : (
            <h3
              onClick={() => setDeactivatedVisible((v) => !v)}
              style={{
                cursor: 'pointer',
                paddingLeft: '12px',
                paddingBottom: '36px'
              }}
            >
              Expand to edit
            </h3>
          )}
        </Box>
      </NotificationBarProvider>
    </Paper>
  )
}

const UserListPage = (): JSX.Element => (
  <AdminPage>
    <UserListContent />
  </AdminPage>
)

export default UserListPage
